/**
 * GLAYVIA NAILS - SHARED JAVASCRIPT
 * Interactive functionality for Nails Collection Page
 * - Filter drawer toggle
 * - FAQ accordion
 * - Quick add to cart
 * - Smooth scroll for anchor links
 * - Sticky filterbar behavior
 */

(function() {
  'use strict';

  // ============================================
  // FILTER DRAWER FUNCTIONALITY
  // ============================================
  
  function initFilterDrawer() {
    const filterTrigger = document.querySelector('[data-filter-trigger]');
    const filterClose = document.querySelector('[data-filter-close]');
    const filterOverlay = document.querySelector('[data-filter-overlay]');
    const filterDrawer = document.querySelector('[data-filter-drawer]');
    const applyFiltersBtn = document.querySelector('[data-apply-filters]');
    const clearFiltersBtn = document.querySelector('[data-clear-filters]');

    if (!filterTrigger || !filterDrawer) return;

    // Open drawer
    function openDrawer() {
      filterDrawer.classList.add('is-active');
      filterOverlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    // Close drawer
    function closeDrawer() {
      filterDrawer.classList.remove('is-active');
      filterOverlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    // Event listeners
    filterTrigger.addEventListener('click', openDrawer);
    if (filterClose) filterClose.addEventListener('click', closeDrawer);
    if (filterOverlay) filterOverlay.addEventListener('click', closeDrawer);
    if (applyFiltersBtn) applyFiltersBtn.addEventListener('click', closeDrawer);

    // Close on ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && filterDrawer.classList.contains('is-active')) {
        closeDrawer();
      }
    });

    // Handle filter checkboxes and update URL
    const filterCheckboxes = filterDrawer.querySelectorAll('input[type="checkbox"]');
    filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', updateActiveFilters);
    });

    // Clear all filters
    if (clearFiltersBtn) {
      clearFiltersBtn.addEventListener('click', function() {
        filterCheckboxes.forEach(checkbox => {
          checkbox.checked = false;
        });
        updateActiveFilters();
        // Optionally reload page without filters
        window.location.href = window.location.pathname;
      });
    }
  }

  // ============================================
  // ACTIVE FILTER CHIPS
  // ============================================

  function updateActiveFilters() {
    const activeFiltersContainer = document.querySelector('[data-active-filters]');
    if (!activeFiltersContainer) return;

    const checkedFilters = document.querySelectorAll('[data-filter-drawer] input[type="checkbox"]:checked');
    activeFiltersContainer.innerHTML = '';

    checkedFilters.forEach(checkbox => {
      const chipHTML = `
        <div class="gl-nails-filter-chip">
          <span>${checkbox.value}</span>
          <button type="button" class="gl-nails-filter-chip__remove" data-remove-filter="${checkbox.value}">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M10.5 3.5l-7 7M3.5 3.5l7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;
      activeFiltersContainer.insertAdjacentHTML('beforeend', chipHTML);
    });

    // Add event listeners to remove buttons
    const removeButtons = activeFiltersContainer.querySelectorAll('[data-remove-filter]');
    removeButtons.forEach(btn => {
      btn.addEventListener('click', function() {
        const filterValue = this.getAttribute('data-remove-filter');
        const checkbox = document.querySelector(`[data-filter-drawer] input[value="${filterValue}"]`);
        if (checkbox) {
          checkbox.checked = false;
          updateActiveFilters();
        }
      });
    });
  }

  // ============================================
  // SORT FUNCTIONALITY
  // ============================================

  function initSortSelect() {
    const sortSelect = document.querySelector('[data-sort-select]');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', function() {
      const selectedValue = this.value;
      const url = new URL(window.location.href);
      
      // Update URL with sort parameter
      if (selectedValue && selectedValue !== 'manual') {
        url.searchParams.set('sort_by', selectedValue);
      } else {
        url.searchParams.delete('sort_by');
      }
      
      window.location.href = url.toString();
    });

    // Set current sort value from URL
    const urlParams = new URLSearchParams(window.location.search);
    const currentSort = urlParams.get('sort_by');
    if (currentSort) {
      sortSelect.value = currentSort;
    }
  }

  // ============================================
  // STICKY FILTERBAR
  // ============================================

  function initStickyFilterbar() {
    const filterbar = document.querySelector('.gl-nails-filterbar');
    if (!filterbar) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          filterbar.classList.add('is-stuck');
        } else {
          filterbar.classList.remove('is-stuck');
        }
      },
      { threshold: [1], rootMargin: '-1px 0px 0px 0px' }
    );

    observer.observe(filterbar);
  }

  // ============================================
  // FAQ ACCORDION
  // ============================================

  function initFaqAccordion() {
    const faqItems = document.querySelectorAll('[data-faq-item]');
    if (!faqItems.length) return;

    faqItems.forEach(item => {
      const trigger = item.querySelector('[data-faq-trigger]');
      const answer = item.querySelector('[data-faq-answer]');

      if (!trigger || !answer) return;

      trigger.addEventListener('click', function() {
        const isActive = item.classList.contains('is-active');

        // Close all other FAQ items
        faqItems.forEach(otherItem => {
          if (otherItem !== item) {
            otherItem.classList.remove('is-active');
            const otherTrigger = otherItem.querySelector('[data-faq-trigger]');
            if (otherTrigger) {
              otherTrigger.setAttribute('aria-expanded', 'false');
            }
          }
        });

        // Toggle current item
        if (isActive) {
          item.classList.remove('is-active');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-active');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // ============================================
  // QUICK ADD TO CART
  // ============================================

  function initQuickAdd() {
    const quickAddButtons = document.querySelectorAll('[data-quick-add]');
    if (!quickAddButtons.length) return;

    quickAddButtons.forEach(button => {
      button.addEventListener('click', async function(e) {
        e.preventDefault();
        
        const variantId = this.getAttribute('data-variant-id');
        if (!variantId) return;

        // Add loading state
        const originalText = this.textContent;
        this.textContent = 'Adding...';
        this.disabled = true;
        this.classList.add('gl-nails-loading');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: 1
              }]
            })
          });

          if (response.ok) {
            // Success - show confirmation
            this.textContent = 'Added!';
            this.classList.remove('gl-nails-loading');
            
            // Trigger cart update event (if theme has cart drawer)
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true
            }));

            // Reset button after 2 seconds
            setTimeout(() => {
              this.textContent = originalText;
              this.disabled = false;
            }, 2000);

          } else {
            throw new Error('Failed to add to cart');
          }

        } catch (error) {
          console.error('Error adding to cart:', error);
          this.textContent = 'Error';
          this.classList.remove('gl-nails-loading');
          
          setTimeout(() => {
            this.textContent = originalText;
            this.disabled = false;
          }, 2000);
        }
      });
    });
  }

  // ============================================
  // SMOOTH SCROLL FOR ANCHOR LINKS
  // ============================================

  function initSmoothScroll() {
    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    
    anchorLinks.forEach(link => {
      link.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        
        // Skip if it's just "#" or empty
        if (!href || href === '#') return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          
          const offset = 80; // Account for sticky header
          const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - offset;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  // ============================================
  // INITIALIZE ALL FUNCTIONALITY
  // ============================================

  function init() {
    initFilterDrawer();
    updateActiveFilters();
    initSortSelect();
    initStickyFilterbar();
    initFaqAccordion();
    initQuickAdd();
    initSmoothScroll();
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', function(event) {
    const sectionId = event.detail.sectionId;
    console.log('Section loaded:', sectionId);
    init();
  });

})();
